import type { HTMLAttributes } from "react"
import { cn } from "@/lib/utils"
import { type GlyphState, StatusGlyph } from "./status-dot"

/** The system's states (refined-system §2.3). */
export type PillTone = "neutral" | "live" | "needs" | "blocked" | "risk" | "wait" | "done"
/**
 * Old tone names, kept for one wave: `running` → live, `ok` → done, `brand` → neutral, `outline` → wait. `gate`
 * (amber "needs a person") → risk: cobalt (`needs`) is only for what waits on the viewer, and callers say so.
 */
type OldTone = "running" | "gate" | "ok" | "brand" | "outline"
export type BadgeTone = PillTone | OldTone

const toPill: Record<BadgeTone, PillTone> = {
  neutral: "neutral",
  live: "live",
  needs: "needs",
  blocked: "blocked",
  risk: "risk",
  wait: "wait",
  done: "done",
  running: "live",
  gate: "risk",
  ok: "done",
  brand: "neutral",
  outline: "wait",
}

const pillTones: Record<PillTone, string> = {
  neutral: "bg-fill text-ink-2",
  live: "bg-live-soft text-live",
  needs: "bg-needs-soft text-needs",
  blocked: "bg-blocked-soft text-blocked",
  risk: "bg-risk-soft text-risk-ink",
  wait: "bg-fill text-wait-ink",
  done: "bg-fill text-ink",
}

const kickerTones: Record<PillTone, string> = {
  neutral: "text-muted",
  live: "text-live",
  needs: "text-needs",
  blocked: "text-blocked",
  risk: "text-risk-text",
  wait: "text-muted",
  done: "text-ink",
}

const glyphOf: Record<PillTone, GlyphState> = {
  neutral: "wait",
  live: "live",
  needs: "needs",
  blocked: "blocked",
  risk: "risk",
  wait: "wait",
  done: "done",
}

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone | undefined
}

/** A kicker label (sans 11 600 uppercase, no fill), as in "QUESTION", "MEMORY · UNTRUSTED". */
export function Badge({ tone = "neutral", className, ...props }: BadgeProps) {
  return (
    <span
      className={cn("kicker inline-flex items-center", kickerTones[toPill[tone]], className)}
      {...props}
    />
  )
}

export interface PillProps extends BadgeProps {
  /** Draw the state's 6px glyph first (refined-system §5.5). */
  glyph?: boolean | undefined
}

/** A status pill: ink on a tint, 20px, radius 3 (refined-system §5.5), as in "running", "PR #412", "Mara · vps-1". */
export function Pill({ tone = "neutral", glyph = false, className, children, ...props }: PillProps) {
  const pill = toPill[tone]
  return (
    <span
      className={cn(
        // Sans 12/500 whatever the parent sets (a pill in a mono row stays sans); never wraps or shrinks in a row.
        "inline-flex min-h-5 max-w-full shrink-0 items-center gap-1.5 rounded-pill px-[7px] py-px font-sans text-[12px] leading-tight font-medium whitespace-nowrap",
        pillTones[pill],
        className,
      )}
      {...props}
    >
      {glyph && <StatusGlyph state={glyphOf[pill]} label={false} />}
      {children}
    </span>
  )
}

/** Old name of `Pill`. */
export const Chip = Pill
